import { ChangeDetectionStrategy, Component, ElementRef, OnInit, computed, input, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SelectionStateService } from 'src/app/services/selection-state.service';
import { EventAccessService } from 'src/app/services/event-access/event-access.service';
import { RoleNavigationService } from 'src/app/services/role-navigation.service';
import { EVENT_ROLE_ORDER, EventRole } from 'src/app/types/event-access.types';
import { UserRoleType } from 'src/app/types/user-role.types';
import { AUTO_HEIGHT_SHEET_CLASS, autoHeightSheetOptions } from 'src/app/utils/auto-height-sheet';
import {
  prefersReducedMotion,
  SHEET_BOUNCE_CLASS,
  sheetBounceEnter,
  sheetBounceLeave,
} from 'src/app/utils/animations/role-switch.animations';
import { EVENT_ROLE_SHEET_SELECT, EventRoleSheetComponent } from './event-role-sheet.component';

/** Misma clase que el sheet de cuenta: esquinas superiores redondeadas (global.scss). */
const ROLE_SHEET_CLASS = 'account-sheet-modal';

/** `ion-app` también tiene la clase `ion-page`, pero deslizarlo arrastraría toasts y overlays. */
const ROUTED_PAGE = '.ion-page:not(ion-app)';

function isEventRole(role: UserRoleType): role is EventRole {
  return (EVENT_ROLE_ORDER as readonly UserRoleType[]).includes(role);
}

/**
 * Chip del header dentro de un evento: muestra el rol activo y permite cambiarlo por otro
 * rol del mismo evento. Solo para usuarios `sellers`; se proyecta con `headerChip`.
 */
@Component({
  selector: 'app-event-role-chip',
  templateUrl: './event-role-chip.component.html',
  styleUrls: ['./event-role-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EventRoleChipComponent implements OnInit {
  readonly eventId = input.required<number>();

  readonly visible = this.selectionState.isSellersUser;
  /** Abreviada para que entre en el header junto al título. */
  readonly labelKey = computed(() => `event-access.chip.labels.${this.selectionState.selectedRole()}`);
  /** Nombre completo, para el lector de pantalla. */
  readonly fullLabelKey = computed(() => `event-access.roles.${this.selectionState.selectedRole()}`);

  private readonly _roles = signal<readonly EventRole[]>([]);

  /** Si el evento no aparece en ninguna fuente, el sheet ofrece solo el rol activo. */
  readonly sheetRoles = computed<readonly EventRole[]>(() => {
    const active = this.selectionState.selectedRole();
    if (this._roles().length) {
      return this._roles();
    }
    return isEventRole(active) ? [active] : [];
  });

  /** Con un solo rol no hay nada que elegir: sin chevron ni sheet. */
  readonly canSwitch = computed(() => this.sheetRoles().length > 1);

  constructor(
    private readonly selectionState: SelectionStateService,
    private readonly eventAccess: EventAccessService,
    private readonly roleNavigation: RoleNavigationService,
    private readonly modalCtrl: ModalController,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {}

  async ngOnInit(): Promise<void> {
    if (!this.visible()) {
      return;
    }
    this._roles.set(this.eventAccess.rolesFor(this.eventId()));
    // Deep link: el evento puede no estar en caché todavía (las fuentes que fallan no rompen la carga).
    this._roles.set(await this.eventAccess.ensureRolesFor(this.eventId()));
  }

  /** La página externa, no la de la pestaña: la barra de tabs vive afuera y tiene que acompañar el slide. */
  private outermostPage(): HTMLElement | null {
    let page = this.elementRef.nativeElement.closest<HTMLElement>(ROUTED_PAGE);
    let parent = page?.parentElement?.closest<HTMLElement>(ROUTED_PAGE) ?? null;
    while (parent) {
      page = parent;
      parent = page.parentElement?.closest<HTMLElement>(ROUTED_PAGE) ?? null;
    }
    return page;
  }

  async openSheet(): Promise<void> {
    if (!this.canSwitch()) {
      return;
    }
    const modal = await this.modalCtrl.create({
      component: EventRoleSheetComponent,
      componentProps: { roles: this.sheetRoles(), activeRole: this.selectionState.selectedRole() },
      ...autoHeightSheetOptions(),
      cssClass: [AUTO_HEIGHT_SHEET_CLASS, ROLE_SHEET_CLASS, SHEET_BOUNCE_CLASS],
      handle: false,
      canDismiss: true,
      animated: !prefersReducedMotion(),
      enterAnimation: sheetBounceEnter,
      leaveAnimation: sheetBounceLeave,
    });
    await modal.present();
    // Después de cerrar, no durante: primero termina el rebote del sheet y recién ahí se mueve la página.
    const { data, role } = await modal.onDidDismiss<EventRole>();
    if (role === EVENT_ROLE_SHEET_SELECT && data) {
      await this.roleNavigation.switchEventRole(data, this.eventId(), this.outermostPage());
    }
  }
}
